<template>
    <div id="fulfillment-process-modal">
        <ProcessModal
            data-qa="fulfillment-modal"
            size="fullscreen"
            :is-open="isOpen"
            :title="$t('funnel.edit.fulfillment.modal.title')"
            :action-button-label="actionButtonLabel"
            show-save
            @close-modal="closeModal"
            @update-config="nextStage"
        >
            <template #additionalHeaderContent>
                <div class="save-status">
                    <DsIcon v-if="lastSavedLabel" name="check-circle" class="save-timestamp-icon" />
                    <div v-if="lastSavedLabel" class="save-timestamp">
                        {{ lastSavedLabel }}
                    </div>
                </div>
                <FulfillmentHeaderMenu
                    data-qa="fulfillment-header-menu"
                    class="menu-button"
                    :users="testUsers"
                    :copy-options="copyOptions"
                    :options-loaded="optionsLoaded"
                    :cell-is-ready="cellIsReady"
                    @menu-item-clicked="menuItemClicked"
                    @toggle-cell-ready="handleToggleReady"
                    @load-copy-options="loadCampaigns"
                />
            </template>
            <template #processParameters>
                <TabStageNavigation
                    v-if="!invalidData"
                    data-qa="tab-stage-navigation"
                    :current-stage-step="selectedStage"
                    :configuration="tabsStageConfig"
                    @previous-tab="showPreviousConfigTab"
                    @next-tab="showNextConfigTab"
                >
                    <template #stage-0>
                        <FulfillmentDelivery
                            v-if="fulfillmentConfigReady"
                            data-qa="fulfillment-delivery"
                            :cell="localCell"
                            :force-invalid="forceInvalid"
                            :fulfillment-object="fulfillmentConfig"
                            :fulfillment-options="fulfillmentOptions"
                            @update-file-usage="updateFileUsage"
                            @update-fulfillment-object="($event) => fulfillmentConfig = $event"
                        />
                    </template>
                    <template #stage-1>
                        <FulfillmentBatch
                            v-if="fulfillmentConfigReady"
                            data-qa="fulfillment-batch"
                            :cell="localCell"
                            :fulfillment-object="fulfillmentConfig"
                            @update-fulfillment-object="($event) => fulfillmentConfig = $event"
                        />
                    </template>
                    <template #stage-2>
                        <FulfillmentContactList
                            v-if="fulfillmentConfigReady"
                            data-qa="fulfillment-contact-list"
                            :cell="localCell"
                            :fulfillment-object="fulfillmentConfig"
                            :fulfillment-options="fulfillmentOptions"
                            @update-fulfillment-object="($event) => fulfillmentConfig = $event"
                            @update-fulfillment-options="($event) => fulfillmentOptions = $event"
                        />
                    </template>
                </TabStageNavigation>
            </template>
        </ProcessModal>
    </div>
</template>

<script>
import ProcessModal from '../processModal/ProcessModal.vue';
import TabStageNavigation from '@/components/shared/TabStageNavigation.vue';
import FulfillmentHeaderMenu from '../fulfillmentHeaderMenu/FulfillmentHeaderMenu.vue';
import FulfillmentDelivery from '../fulfillmentDelivery/FulfillmentDelivery.vue';
import FulfillmentBatch from '../fulfillmentBatch/FulfillmentBatch.vue';
import FulfillmentContactList from '../fulfillmentContactList/FulfillmentContactList.vue';
import {
    saveMarketingFulfillment,
    createMarketingFulfillment,
    copyMarketingFulfillment,
    testMarketingFulfillment,
    loadFulfillmentOptions,
    listFilteredCampaigns,
    listFilteredSequences,

} from './fulfillment.api';
import {
    FULFILLMENT_DELIVERY_TYPE,
    FULFILLMENT_CONFIG_STAGE,
    FULFILLMENT_ATTACHMENT_TYPE,
    FULFILLMENT_ATTACHMENT_PERCENT_THRESHOLD,
    FULFILLMENT_MENU_ITEMS,
} from '../../../../utils/editor.constants';
import { fromObjectToOptions } from './fulfillment.util';

export default {
    FULFILLMENT_DELIVERY_TYPE,
    FULFILLMENT_CONFIG_STAGE,
    FULFILLMENT_ATTACHMENT_TYPE,
    FULFILLMENT_ATTACHMENT_PERCENT_THRESHOLD,
    components: {
        ProcessModal,
        TabStageNavigation,
        FulfillmentDelivery,
        FulfillmentBatch,
        FulfillmentContactList,
        FulfillmentHeaderMenu,
    },


    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        cell: {
            type: Object,
            default: () => ({
                value: {
                },
            }),
        },
    },

    emits: ['close-modal', 'update-cell-value', 'toggle-cell-ready'],

    data() {
        return {
            fulfillmentId: null,
            invalidData: false,
            forceInvalid: false,
            optionsLoaded: false,
            fileUsageAlert: false,
            selectedStage: 0,
            lastSavedTimestamp: 0,
            copyOptions: [],
            campaignOptions: [],
            fulfillmentConfig: {},
            fulfillmentOptions: {},
            localCell: { ...this.cell },
        };
    },

    computed: {
        tabsStageConfig () {
            return [
                {
                    stageStep: FULFILLMENT_CONFIG_STAGE.DELIVERY,
                    navigationIconTitle: 1,
                    navigationSubtitle: this.$t('funnel.edit.fulfillment.delivery'),
                    slotName: 'stage-0',
                },
                {
                    stageStep: FULFILLMENT_CONFIG_STAGE.BATCH,
                    navigationIconTitle: 2,
                    navigationSubtitle: this.$t('funnel.edit.fulfillment.batching'),
                    slotName: 'stage-1',
                },
                {
                    stageStep: FULFILLMENT_CONFIG_STAGE.CONTACT_LIST,
                    navigationIconTitle: 3,
                    navigationSubtitle: this.$t('funnel.edit.fulfillment.create.list'),
                    slotName: 'stage-2',
                },
            ];
        },

        fulfillmentConfigReady() {
            return this.fulfillmentConfig !== null && Object.keys(this.fulfillmentConfig).length > 0;
        },

        actionButtonLabel() {
            return this.selectedStage < FULFILLMENT_CONFIG_STAGE.CONTACT_LIST
                ? this.$t('funnel.edit.fulfillment.next')
                : this.$t('funnel.edit.fulfillment.done');
        },

        lastSavedLabel() {
            if (!this.lastSavedTimestamp) return null;

            const dateString = new Date(this.lastSavedTimestamp)
                .toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
                .replace(' ', '')
                .toLowerCase();

            return `${this.$t('funnel.header.saved.at')} ${dateString}`;
        },

        cellIsReady() {
            return this.localCell.value?.ready === '1';
        },

        testUsers() {
            let response = [];

            if (this.fulfillmentOptions && this.fulfillmentOptions) {
                const users = fromObjectToOptions(this.fulfillmentOptions.usersMap || {});

                response = users.filter((element) => {
                    return element.value !== FULFILLMENT_DELIVERY_TYPE.OWNER_DEFAULT_TYPE;
                });
            }

            return response;
        },
    },

    watch: {
        cell: {
            handler() {
                this.localCell = { ...this.cell };
            },
            deep: true,
        },
    },

    mounted() {
        this.loadMarketingFulfillment();
    },

    methods: {
        async loadMarketingFulfillment() {
            if (!this.localCell.value?.marketingFulfillmentId) {
                const name = this.localCell.value?.name || this.$t('funnel.sidebar.then.cell.fulfillment.label');

                this.fulfillmentId = await createMarketingFulfillment(name);

                if (this.fulfillmentId) {
                    this.localCell.value.marketingFulfillmentId = this.fulfillmentId;
                    await this.loadConfigObjectData();
                }
            } else {
                this.fulfillmentId = this.localCell.value.marketingFulfillmentId;
                await this.loadConfigObjectData();
            }
        },

        async loadConfigObjectData() {
            this.fulfillmentOptions = await loadFulfillmentOptions(this.fulfillmentId);
            this.initializeConfigObject(this.fulfillmentOptions);
        },

        initializeConfigObject(initializer) {
            this.fulfillmentConfig = {
                deliveryType: initializer.deliveryType,
                attachmentType: initializer.attachmentType,
                batch: initializer.batch,
                minItems: initializer.minItems,
                maxWaitDays: initializer.maxWaitDays,
                assignedUserId: initializer.userId,
                instructions: initializer.instructions,
                notify: initializer.notify,
                fromKey: initializer.fromKey,
                fromName: initializer.fromName,
                fromEmail: initializer.fromEmail,
                toKey: initializer.toKey,
                toName: initializer.toName,
                toEmail: initializer.toEmail,
                subject: initializer.subject,
                htmlBody: initializer.htmlBody,
                fieldNames: null,
                fieldLabels: null,
                fileBoxIds: initializer.fileBoxIds,
                pendingExportFields: null,
            };

            this.initializeExportData();
            this.initializeFieldCategoryOptions();
        },

        closeModal() {
            this.$emit('close-modal');
        },

        async updateConfig() {
            if (this.isReady()) {
                this.removeEmptyExportFields();
                const eventData = {
                    cell: this.localCell,
                    preventClose: true,
                };

                this.$emit('update-cell-value', eventData);

                try {
                    await saveMarketingFulfillment(this.fulfillmentId, this.fulfillmentConfig);
                    this.lastSavedTimestamp = Date.now();
                } catch (error) {
                    this.$error({
                        message: error.message || this.$t('funnel.edit.fulfillment.saving.error'),
                        bottom: true,
                    });
                }
            }
        },

        nextStage() {
            if (this.selectedStage === FULFILLMENT_CONFIG_STAGE.CONTACT_LIST) {
                this.$nextTick(() => {
                    this.closeModal();
                });
            }
            this.showNextConfigTab(this.selectedStage);
        },

        isReady() {
            this.setForceInvalid(false);
            let response = true;

            for (const stage of this.tabsStageConfig) {
                if ('stageStep' in stage) {
                    response = response && this.isStageReady(stage.stageStep);
                }
            }

            this.setForceInvalid(!response);

            return response;
        },

        setForceInvalid (value) {
            this.forceInvalid = value;

            if (this.forceInvalid) {
                this.$error({
                    message: this.$t('funnel.edit.fulfillment.validation.error.text'),
                    bottom: true,
                });
            }
        },

        isStageReady(stageId) {
            switch (stageId) {
            case FULFILLMENT_CONFIG_STAGE.DELIVERY:
                return this.isDeliveryConfigReady();
            case FULFILLMENT_CONFIG_STAGE.BATCH:
            case FULFILLMENT_CONFIG_STAGE.CONTACT_LIST:
                return true;
            default:
                return false;
            }
        },

        isDeliveryConfigReady() {
            let response = true;

            if (this.fulfillmentConfig.deliveryType === FULFILLMENT_DELIVERY_TYPE.EMAIL) {
                if (!this.fulfillmentConfig.fromKey || !this.fulfillmentConfig.toKey) {
                    response = false;
                }

                if (!this.fulfillmentConfig.subject || !this.fulfillmentConfig.htmlBody) {
                    response = false;
                }

                if (this.fulfillmentConfig.fromKey === FULFILLMENT_DELIVERY_TYPE.OTHER_FROM_TYPE
                    && !this.fulfillmentConfig.fromEmail) {
                    response = false;
                }

                if (this.fulfillmentConfig.toKey === FULFILLMENT_DELIVERY_TYPE.OTHER_FROM_TYPE
                        && !this.fulfillmentConfig.toEmail) {
                    response = false;
                }

                if (this.fulfillmentConfig.attachmentType === FULFILLMENT_ATTACHMENT_TYPE.FILE
                    && this.fileUsageAlert) {
                    response = false;
                }
            } else if (this.fulfillmentConfig.deliveryType === FULFILLMENT_DELIVERY_TYPE.DASHBOARD) {
                if (!this.fulfillmentConfig.instructions || !this.fulfillmentConfig.assignedUserId) {
                    response = false;
                }
            }

            return response;
        },

        showNextConfigTab(stageId) {
            this.setForceInvalid(false);

            if (this.isStageReady(stageId)) {
                this.updateConfig();
                this.selectedStage = Math.min(this.selectedStage + 1, FULFILLMENT_CONFIG_STAGE.CONTACT_LIST);
            } else {
                this.setForceInvalid(true);
            }
        },

        showPreviousConfigTab(stageId) {
            this.selectedStage = stageId;
        },

        removeEmptyExportFields() {
            const { fieldNames, fieldLabels } = this.fulfillmentConfig;
            const { exportFields } = this.fulfillmentOptions;

            if (fieldNames?.length === fieldLabels?.length && fieldLabels?.length === exportFields?.length) {
                const isNotEmpty = (value, index) => value && fieldLabels[index] && exportFields[index].name && exportFields[index].label;

                this.fulfillmentConfig.fieldNames = fieldNames.filter(isNotEmpty);
                this.fulfillmentConfig.fieldLabels = fieldLabels.filter(isNotEmpty);
                this.fulfillmentOptions.exportFields = exportFields.filter((_, index) => isNotEmpty(fieldNames[index], index));
            }
        },

        initializeExportData() {
            this.fulfillmentConfig.fieldNames = [];
            this.fulfillmentConfig.fieldLabels = [];

            for (const field of this.fulfillmentOptions.exportFields) {
                if ('name' in field) {
                    this.fulfillmentConfig.fieldNames.push(field.name);
                }

                if ('label' in field) {
                    this.fulfillmentConfig.fieldLabels.push(field.label);
                }
            }
        },

        initializeFieldCategoryOptions() {
            this.fulfillmentOptions.fieldCategoryOptions = this.fulfillmentOptions.exportFieldsOptions.reduce((categories, currentValue) => {
                const category = currentValue.name.split('.')[0];
                const existingCategory = categories.find((item) => item.category.value === category);

                if (existingCategory) {
                    existingCategory.options.push({ label: currentValue.label, value: currentValue.name });
                } else {
                    categories.push({
                        category: {
                            label: category,
                            value: category,
                        },
                        options: [{ label: currentValue.label, value: currentValue.name }],
                    });
                }

                return categories;
            }, []);
        },

        updateFileUsage(fileUsage) {
            this.fileUsageAlert = fileUsage === FULFILLMENT_ATTACHMENT_PERCENT_THRESHOLD;
        },

        menuItemClicked({ optionId, data }) {
            switch (optionId) {
            case FULFILLMENT_MENU_ITEMS.TEST.ID:
                this.handleTest(data);
                break;
            case FULFILLMENT_MENU_ITEMS.COPY.ID:
                this.handleCopy(data);
                break;
            default:
                break;
            }
        },

        handleToggleReady() {
            if (this.cellIsReady) {
                this.$emit('toggle-cell-ready', { cell: this.localCell, preventClose: true });
            } else {
                this.updateConfig();

                if (this.forceInvalid) {
                    this.$toast({ message: this.$t('funnel.edit.fulfillment.toggle.message.error') });
                }
            }
        },

        async handleTest(userId) {
            const data = await testMarketingFulfillment(this.fulfillmentId, userId);

            this.$toast({ message: data });
        },

        async handleCopy(copyFromId) {
            this.invalidData = true;
            this.fulfillmentOptions = await copyMarketingFulfillment(this.fulfillmentId, copyFromId);
            this.initializeConfigObject(this.fulfillmentOptions);

            this.$nextTick(() => {
                this.invalidData = false;
                this.$toast({ message: this.$t('funnel.edit.fulfillment.copy.message') });
            });
        },

        async loadCampaigns() {
            this.campaignOptions = await listFilteredCampaigns();

            await this.loadSequences();
        },

        async loadSequences() {
            const promises = [];

            for (const campaign of this.campaignOptions) {
                if ('value' in campaign && 'label' in campaign) {
                    const payload = {
                        value: campaign.value,
                    };

                    const promise = listFilteredSequences(payload)
                        .then((dataArray) => {
                            for (const option of dataArray) {
                                if ('value' in option && 'label' in option) {
                                    this.copyOptions.push({ value: option.value, label: option.label, subtext: campaign.label });
                                }
                            }
                        });

                    promises.push(promise);
                }
            }
            await Promise.all(promises);
            this.optionsLoaded = true;
        },
    },
};
</script>
<style lang="scss" scoped>
.save-status {
    display: flex;
    padding-top: $spacing-150;

    .save-timestamp {
        color: $color-green;
        font-size: $font-size-sm;
        margin-right: $spacing-125;
    }
    svg.save-timestamp-icon {
        --icon-size: 1rem;
        color: $color-green;
        margin-right: $spacing-050;
    }
}
.menu-button {
    margin-right: $spacing-200;
}
</style>
<style lang="scss">
div#fulfillment-process-modal div.modal-body {
    min-height: calc(100vh - 56px);
}
</style>
